import {useCopilotSettings} from '@github-ui/code-view-shared/contexts/CopilotSettingsContext'
import {usePath} from '@github-ui/code-view-shared/contexts/PathContext'
import {useRefInfo} from '@github-ui/code-view-shared/contexts/RefInfoContext'
import type {CopilotChatReference} from '@github-ui/copilot-chat-types'
import CopilotChatButton from '@github-ui/copilot-code-chat/CopilotCodeLinesMenu'
import {useCurrentRepository} from '@github-ui/current-repository'
import type {RefType} from '@github-ui/ref-utils'
import {qualifyRef} from '@github-ui/ref-utils'
import React, {useEffect, useImperativeHandle, useMemo, useState} from 'react'
import {createPortal} from 'react-dom'

import {useCurrentBlob} from '../../../hooks/CurrentBlob'
import {buttonHeight} from '../../../hooks/use-highlighted-line-menu-options'
import {useHighlightedLinesRange} from './Code/HighlightedLineProvider'

const positionerID = 'copilot-button-positioner'
const portalRootID = 'copilot-button-container'

export function CopilotButtonContainer({children}: React.PropsWithChildren) {
  return (
    <div id={positionerID} className="position-relative">
      {children}
      <div id={portalRootID} />
    </div>
  )
}

interface CopilotButtonProps {
  rowBeginNumber: number
  rowEndNumber: number
  id: string
  recalcPosition?: number
  ref?: React.Ref<CopilotButtonHandle>
}

export interface CopilotButtonHandle {
  setAnchor(anchor: HTMLElement | null): void
}

// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
export const CopilotButton = React.memo(function CopilotButton({
  rowBeginNumber,
  rowEndNumber,
  id,
  recalcPosition,
  ref,
}: CopilotButtonProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null)
  useImperativeHandle(ref, () => ({setAnchor}))

  const [position, setPosition] = useState<React.CSSProperties | undefined>(undefined)

  // We need to wait for the virtualization render in order to know where this position should be
  // due to possible word wrapping.
  // Note: useEffect (not useLayoutEffect) is intentional — the rAF below defers the DOM read to the
  // next frame anyway, so useLayoutEffect would only block paint without preventing flicker.
  useEffect(() => {
    let rafId = 0
    const changePosition = () => {
      cancelAnimationFrame(rafId)
      rafId = requestAnimationFrame(() => setPosition(calculateCopilotButtonPosition(anchor)))
    }
    changePosition()

    // eslint-disable-next-line github/prefer-observers
    window.addEventListener('resize', changePosition)

    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('resize', changePosition)
    }
  }, [anchor, recalcPosition])

  // Copilot Menu Actions
  const path = usePath()
  const {copilotAccessAllowed} = useCopilotSettings()
  const refInfo = useRefInfo()
  const selectedBlobRange = useHighlightedLinesRange()
  const repo = useCurrentRepository()
  const {language, languageID, copilotSWEAgentEnabled} = useCurrentBlob()
  const messageReference: CopilotChatReference = useMemo(
    () => ({
      type: 'snippet',
      languageID,
      languageName: language,
      path,
      range: {start: rowBeginNumber, end: rowEndNumber},
      ref: qualifyRef(refInfo.name, refInfo.refType as RefType),
      commitOID: refInfo.currentOid,
      repoID: repo.id,
      repoName: repo.name,
      repoOwner: repo.ownerLogin,
      url: window.location.href,
    }),
    [languageID, language, path, rowBeginNumber, rowEndNumber, refInfo, repo],
  )

  const showAgentTaskButton = copilotSWEAgentEnabled

  const element =
    position === undefined ? null : (
      <div
        style={{
          alignSelf: 'center',
          position: 'absolute',
          lineHeight: '16px',
          height: '24px',
          width: '24px',
          zIndex: 2,
          ...position,
        }}
      >
        <CopilotChatButton
          copilotAccessAllowed={copilotAccessAllowed}
          messageReference={messageReference}
          id={id}
          showAgentTaskButton={showAgentTaskButton}
          currentRepo={repo}
          currentReference={{
            path,
            range: selectedBlobRange
              ? {start: selectedBlobRange.start.line, end: selectedBlobRange.end.line}
              : undefined,
          }}
        />
      </div>
    )

  const portalRoot = document.getElementById(portalRootID)

  return portalRoot ? createPortal(element, portalRoot) : null
})

export function calculateCopilotButtonPosition(anchor: HTMLElement | null, offset = {x: 0, y: 0}): React.CSSProperties {
  const container = document.getElementById(positionerID)
  if (!anchor || !container) {
    return {display: 'none'}
  }

  const {top: anchorTop, height: anchorHeight} = anchor.getBoundingClientRect()
  const {top: tableTop} = container.getBoundingClientRect()
  const topOffset = (buttonHeight - anchorHeight) / 2

  return {
    top: `${anchorTop - tableTop - topOffset + offset.y + 1}px`,
    // Don't need to calculate this. This provides 8px spacing between
    // the edge of the button and edge of the blob
    right: '37px',
  }
}
